import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL;

// TYPES
export type UserType = 'personne' | 'entreprise' | 'professionnel' | '';
export type UserStatus = 'active' | 'anonymous' | 'waiting_validation' | '';

export interface MenuClickStatsParams {
  period?: string;
  from?: string;
  to?: string;
  user_id?: string;
  user_type?: UserType;
  status?: UserStatus;
  menu_key?: string;
  device_type?: string;
  group_by?: 'user' | 'menu' | 'day' | 'hour';
  page?: number;
  limit?: number;
}

export interface ExportStatsParams {
  period?: string;
  from?: string;
  to?: string;
  user_id?: string;
  user_type?: UserType;
  status?: UserStatus;
  menu_key?: string;
  device_type?: string;
  format?: 'csv';
}

// Interface pour la réponse API
export interface MenuClickStatsResponse {
  kpis: {
    total_clicks: number;
    unique_users: number;
    menus_used: number;
  };
  data: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface HourlyClickPoint {
  hour:    number;
  label:   string; 
  logins:  number;
  logouts: number;
}

export interface ClickStatsResponse {
  loginClicks:  number;
  logoutClicks: number;
  hourly:       HourlyClickPoint[];
}

// Génère un session_id unique par onglet/session (stocké en sessionStorage)
const getSessionId = (): string => {
  try {
    let sessionId = sessionStorage.getItem('app_session_id');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem('app_session_id', sessionId);
    }
    return sessionId;
  } catch (error) {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }
};

let previousMenuKey: string | null = null;

interface TrackMenuClickPayload {
  menu_key: string;
  menu_label: string;
  previous_menu_key?: string | null;
  session_id?: string;
  user_id?: number;
}

/**
 * Envoie un événement de clic menu au backend
 */
export const trackMenuClick = (menuKey: string, menuLabel: string): void => {
  try {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    
    const payload: TrackMenuClickPayload = {
      menu_key: menuKey,
      menu_label: menuLabel,
      previous_menu_key: previousMenuKey,
      session_id: getSessionId(),
    };

    if (userId) {
      payload.user_id = parseInt(userId, 10);
    }

    previousMenuKey = menuKey;

    axios.post(`${API_URL}/menu-clicks`, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': token }),
      },
      timeout: 3000,
    }).catch(() => {});
  } catch (error) {}
};

/**
 * Récupère les statistiques des clics menus
 */
export const getMenuClickStats = async (params: MenuClickStatsParams = {}): Promise<MenuClickStatsResponse | null> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const queryParams = new URLSearchParams();
    
    // Ajouter les paramètres de filtre uniquement s'ils ont des valeurs
    if (params.period) queryParams.append('period', params.period);
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    
    // ajouter les filtres user_type / status si une valeur est fournie.
    // la vérification "params.user_type" suffit : elle exclut undefined
    // et la chaîne vide (falsy), évitant ainsi l'erreur TS2367.
    if (params.user_type) {
      queryParams.append('user_type', params.user_type);
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }
    
    if (params.menu_key) queryParams.append('menu_key', params.menu_key);
    if (params.device_type) queryParams.append('device_type', params.device_type);
    if (params.group_by) queryParams.append('group_by', params.group_by);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await axios.get(`${API_URL}/menu-clicks/stats`, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      toast.error(`Erreur lors du chargement des statistiques: ${response.data?.message || 'Erreur inconnue'}`);
      return null;
    }
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Erreur lors du chargement des statistiques');
      throw errorData;
    } else {
      toast.error('Une erreur réseau est survenue');
      throw new Error('Une erreur réseau est survenue');
    }
  }
};

/**
 * Récupère les KPIs des clics menus
 */
export const getMenuClickKPIs = async (params: Omit<MenuClickStatsParams, 'group_by' | 'page' | 'limit'> = {}): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const queryParams = new URLSearchParams();
    
    if (params.period) queryParams.append('period', params.period);
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.user_type) queryParams.append('user_type', params.user_type);
    if (params.status) queryParams.append('status', params.status);
    if (params.menu_key) queryParams.append('menu_key', params.menu_key);
    if (params.device_type) queryParams.append('device_type', params.device_type);

    const response = await axios.get(`${API_URL}/menu-clicks/kpis`, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      toast.error(`Erreur lors du chargement des KPIs: ${response.data?.message || 'Erreur inconnue'}`);
      return null;
    }
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw error.response.data;
    } else {
      throw new Error('Une erreur réseau est survenue');
    }
  }
};

// menuClick_service.js - Fonction exportMenuClickStats améliorée

export const exportMenuClickStats = async (params: ExportStatsParams = {}): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const queryParams = new URLSearchParams();
    
    // Ajouter tous les filtres
    if (params.period) queryParams.append('period', params.period);
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.user_type) queryParams.append('user_type', params.user_type);
    if (params.status) queryParams.append('status', params.status);
    if (params.menu_key) queryParams.append('menu_key', params.menu_key);
    if (params.device_type) queryParams.append('device_type', params.device_type);
    queryParams.append('group_by', 'user');

    const response = await axios.get(`${API_URL}/menu-clicks/export`, {
      params: queryParams,
      headers: {
        'Authorization': `${token}`,
      },
      responseType: 'blob',
    });

    if (response.status === 200) {
      // Récupérer le nom du fichier depuis le header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `menu-clicks-export-${new Date().toISOString().split('T')[0]}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Créer un objet URL à partir du blob
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      
      // Créer un lien de téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export démarré avec succès');
    } else {
      toast.error('Erreur lors de l\'export des données');
    }
  } catch (error: any) {
    console.error('Erreur export:', error);
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.message || 'Erreur lors de l\'export');
        } catch {
          toast.error('Erreur lors de l\'export des données');
        }
      } else {
        toast.error(error.response.data?.message || 'Erreur lors de l\'export');
      }
    } else {
      toast.error('Une erreur réseau est survenue');
    }
  }
};
/**
 * Récupère tous les menus uniques qui ont été cliqués
 */
export const getDistinctMenus = async (params: Omit<MenuClickStatsParams, 'group_by' | 'page' | 'limit'> = {}): Promise<string[]> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const queryParams = new URLSearchParams();
    
    if (params.period) queryParams.append('period', params.period);
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.user_type) queryParams.append('user_type', params.user_type);
    if (params.status) queryParams.append('status', params.status);
    if (params.device_type) queryParams.append('device_type', params.device_type);

    const response = await axios.get(`${API_URL}/menu-clicks/menus`, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token}`,
      },
    });

    if (response.status === 200) {
      return response.data.menus || [];
    } else {
      return [];
    }
  } catch (error) {
    console.error('Erreur lors de la récupération des menus:', error);
    return [];
  }
};

/**
 * Réinitialise la session de tracking
 */
export const resetTrackingSession = (): void => {
  try {
    sessionStorage.removeItem('app_session_id');
    previousMenuKey = null;
  } catch (error) {}
};

/**
 * Récupère l'ID de session actuel
 */
export const getCurrentSessionId = (): string | null => {
  try {
    return sessionStorage.getItem('app_session_id');
  } catch {
    return null;
  }
};

// ============================================
// NOUVEAUX TYPES pour le dashboard amélioré
// ============================================

export interface UserSummary {
  user: {
    id: number;
    name: string;
    firstname: string;
    email: string;
    user_type: UserType;
    status: UserStatus;
    fullName: string;
  };
  stats: {
    total_clicks: number;
    distinct_menus: number;
    last_click: string;
  };
  is_live: boolean;
}

export interface UserSummaryResponse {
  data: UserSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface LiveUsersResponse {
  count: number;
  users: number[];
}

export interface UserJourney {
  user: {
    id: number;
    name: string;
    firstname: string;
    email: string;
    user_type: string;
    status: string;
    fullName: string;
    profile_picture: string | null;
    
    last_login_at?: string | null;
    last_login_ip?: string | null;
    last_login_location?: string | null;
    last_login_country?: string | null;
    last_login_city?: string | null;
    last_login_latitude?: number | null;
    last_login_longitude?: number | null;
    last_login_user_agent?: string | null;
  };
  stats: {
    total_clicks: number;
    first_click: string;
    last_click: string;
    total_sessions: number;
    distinct_menus: number;
  };
  journey: Array<{
    step: number;
    menu_key: string;
    menu_label: string;
    clicked_at: string;
    device_type: string;
    ip?:string
    location: {
      city: string;
      country: string;
      latitude: number;
      longitude: number;
    } | null;
    time_from_previous: number; // en millisecondes
    session_id: string;
  }>;
  sessions: Array<{
    session_id: string;
    start: string;
    end: string;
    clicks: Array<{
      menu_label: string;
      clicked_at: string;
    }>;
  }>;
}

// ============================================
// NOUVELLES FONCTIONS API
// ============================================

/**
 * Récupère le résumé des utilisateurs (1 ligne = 1 utilisateur)
 * Pour le nouveau dashboard avec statut connecté/déconnecté
 */
export const getUserSummary = async (params: {
  period?: string;
  from?: string;
  to?: string;
  user_type?: UserType;
  status?: UserStatus;
  search?: string;
  page?: number;
  limit?: number;
} = {}): Promise<UserSummaryResponse | null> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const queryParams = new URLSearchParams();
    
    if (params.period) queryParams.append('period', params.period);
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.user_type) queryParams.append('user_type', params.user_type);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await axios.get(`${API_URL}/menu-clicks/user-summary`, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    } else {
      toast.error(`Erreur lors du chargement des utilisateurs: ${response.data?.message || 'Erreur inconnue'}`);
      return null;
    }
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      toast.error(errorData.message || 'Erreur lors du chargement des utilisateurs');
      throw errorData;
    } else {
      toast.error('Une erreur réseau est survenue');
      throw new Error('Une erreur réseau est survenue');
    }
  }
};

/**
 * Récupère le nombre de visiteurs en direct (actifs dans les 5 dernières minutes)
 */
export const getLiveUsersCount = async (): Promise<number> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const response = await axios.get(`${API_URL}/menu-clicks/live-users`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token}`,
      },
    });

    if (response.status === 200) {
      return response.data.count || 0;
    } else {
      return 0;
    }
  } catch (error: any) {
    console.error('Erreur live users:', error);
    return 0; // Silencieux car c'est un KPI secondaire
  }
};

/**
 * Récupère le parcours détaillé d'un utilisateur (pour la modale de détails)
 */
export const getUserJourney = async (
  userId: number, 
  params?: {
    period?: string;
    from?: string;
    to?: string;
  }
): Promise<UserJourney | { noHistory: true; userId: number } | null> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.warn('Token non trouvé');
      return null;
    }

    const queryParams = new URLSearchParams();
    
    if (params?.period) queryParams.append('period', params.period);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);

    const response = await axios.get(`${API_URL}/menu-clicks/user-journey/${userId}`, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    }
    return { noHistory: true, userId };
    
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      
      if (error.response.status === 404 || 
          errorData.message?.toLowerCase().includes('aucune donnée') ||
          errorData.message?.toLowerCase().includes('no data')) {
        return { noHistory: true, userId };
      }
    }
    console.error('Erreur:', error);
    return null;
  }
};

// ============================================
// FONCTIONS UTILITAIRES pour le nouveau dashboard
// ============================================

/**
 * Formate la durée entre deux clics
 * @param ms Durée en millisecondes
 * @returns Durée formatée (ex: "2min 30s")
 */
export const formatDuration = (ms: number): string => {
  if (!ms || ms < 1000) return 'instant';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}min${remainingSeconds > 0 ? remainingSeconds + 's' : ''}`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Détermine si un utilisateur est considéré comme "en ligne"
 * @param lastClickDate Date du dernier clic
 * @param threshold Minutes de seuil (défaut: 5)
 */
export const isUserLive = (lastClickDate: string, thresholdMinutes: number = 5): boolean => {
  const lastClick = new Date(lastClickDate).getTime();
  const now = Date.now();
  const threshold = thresholdMinutes * 60 * 1000;
  return (now - lastClick) < threshold;
};

/**
 * Récupère l'icône et la couleur du statut de connexion
 */
export const getLiveStatusStyle = (isLive: boolean): { color: string; background: string; label: string; icon: string } => {
  return isLive ? {
    color: '#059669',
    background: '#D1FAE5',
    label: 'Connecté',
    icon: '●' // ou utiliser FaCircle de react-icons
  } : {
    color: '#B91C1C',
    background: '#FEE2E2',
    label: 'Déconnecté',
    icon: '○'
  };
};

export const getClickStats = async (params: {
  period?:   string;
  from?:     string;
  to?:       string;
  userType?: string;
  status?:   string;
}): Promise<ClickStatsResponse> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token non trouvé');

    const queryParams = new URLSearchParams();
    if (params.period)   queryParams.append('period',   params.period);
    if (params.from)     queryParams.append('from',     params.from);
    if (params.to)       queryParams.append('to',       params.to);
    if (params.userType) queryParams.append('userType', params.userType);
    if (params.status)   queryParams.append('status',   params.status);

    const response = await axios.get(`${API_URL}/menu-clicks/click-stats`, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${token}`,
      },
    });

    return response.data as ClickStatsResponse;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw error.response.data;
    }
    throw new Error('Une erreur réseau est survenue');
  }
};

